import moment from 'moment';

import { dateFromLocalToAPI } from '../../../util/dates';

const toApiDay = (date) =>
  dateFromLocalToAPI(moment(date).startOf('day').toDate()).toISOString();

/**
 * Build the `bookingData` for the listing-page price preview
 * (`fetchTransactionLineItems`) from the BookingDatesForm values.
 *
 * `bookingDates` are the form's own moment instances (react-hook-form keeps
 * them by reference). Never call `.utc()` on them: it switches the instance to
 * UTC mode in place, so a local-midnight check-in (19.09 00:00 CEST) then reads
 * as the previous UTC day (18.09) everywhere the form renders it — e.g.
 * `selectedDateOverlapsOpenBooking` blocked the night right after an open
 * annual-pass booking. Serialise from a clone instead; the wire format
 * (UTC ISO string) stays the same.
 *
 * @param {object} params
 * @param {[import('moment').MomentInput, import('moment').MomentInput]} params.bookingDates - Selected [start, end]
 * @param {{adult?: number, child?: number, toddler?: number, pet?: number}} params.guestCount
 * @param {{code: string}|null} [params.voucher]
 * @param {object} [params.extraLineItems]
 * @param {object} [params.customLineItems]
 * @returns {object} bookingData for fetchTransactionLineItems
 */
export const buildLineItemsBookingData = ({
  bookingDates,
  guestCount,
  voucher = null,
  extraLineItems = {},
  customLineItems = {},
}) => {
  const [startDate, endDate] = bookingDates;
  return {
    startDate: toApiDay(startDate),
    endDate: toApiDay(endDate),
    guestCount: {
      adultCount: guestCount.adult,
      childCount: guestCount.child,
      toddlerCount: guestCount.toddler,
      petCount: guestCount.pet,
    },
    ...(voucher ? { voucher } : {}),
    extraLineItems,
    customLineItems,
  };
};

/**
 * How the mobile booking panel opens its booking form. Phones keep the full-screen
 * sheet; from the `sm` breakpoint on (tablets below the desktop sidebar) the form
 * opens as a centred dialog.
 *
 * @param {number} width - Layout width from `useScreenSize`
 * @param {number} dialogMinWidth - First width that gets the dialog (theme `sm` breakpoint)
 * @returns {'sheet' | 'dialog'}
 */
export const getBookingModalVariant = (width, dialogMinWidth) =>
  width >= dialogMinWidth ? 'dialog' : 'sheet';

/**
 * Reduce the form's line item state to the selected items with their listing
 * price, for the price preview. Only entries that are added in the form AND
 * exist on the listing survive; the quantity is clamped to 1..maxQuantity.
 *
 * @param {Object<string, {add?: boolean, quantity?: string|number}>} [formItems] - Form state per line item key
 * @param {Object<string, {price: object}>} listingItems - Line item definitions from the listing publicData
 * @param {number} [maxQuantity=10]
 * @returns {Object<string, {price: object, quantity: number}>}
 */
export const selectLineItems = (formItems, listingItems, maxQuantity = 10) => {
  const result = {};
  if (!formItems) return result;

  Object.keys(formItems).forEach((key) => {
    if (formItems[key]?.add && listingItems[key]) {
      const quantity = Math.min(
        Math.max(1, parseInt(formItems[key].quantity, 10) || 1),
        maxQuantity,
      );
      result[key] = {
        price: listingItems[key].price,
        quantity,
      };
    }
  });
  return result;
};

/**
 * Keep the added form entries and merge in their listing definition.
 *
 * @param {Object<string, {add?: boolean, quantity?: number}>} formItems
 * @param {Object<string, object>} listingItems
 * @param {boolean} withQuantity - Carry the form's `quantity` over (custom items)
 * @returns {Object<string, object>}
 */
const pickAddedItems = (formItems, listingItems, withQuantity) =>
  Object.entries(formItems || {}).reduce((acc, [key, value]) => {
    if (value.add) {
      acc[key] = {
        ...listingItems[key],
        ...(withQuantity ? { quantity: value.quantity } : {}),
      };
    }
    return acc;
  }, {});

/**
 * @typedef {object} SubmitValues
 * @property {{bookingStart: string, bookingEnd: string}} bookingDates
 * @property {object} bookingData
 */

/**
 * Map the BookingDatesForm values to the payload the checkout submit expects.
 *
 * @param {object} values - BookingDatesForm values
 * @param {object} publicData - `currentListing.attributes.publicData`
 * @returns {SubmitValues|null} `null` while the date range is incomplete
 */
export const buildSubmitValues = (values, publicData) => {
  const [bookingStart, bookingEnd] = values.bookingDates || [];
  // The form can submit before both dates are picked, and toISOString() on a
  // null date throws.
  if (!bookingStart || !bookingEnd) {
    return null;
  }
  const extraLineItems = publicData?.extraLineItems || {};
  const customLineItems = publicData?.customLineItems || {};

  return {
    bookingDates: {
      bookingStart: bookingStart.toISOString(),
      bookingEnd: bookingEnd.toISOString(),
    },
    bookingData: {
      guestCount: {
        adultCount: values.guestCount.adult,
        childCount: values.guestCount.child,
        toddlerCount: values.guestCount.toddler,
        petCount: values.guestCount.pet,
      },
      voucher: { code: values.voucherCode },
      extraLineItems: pickAddedItems(values.extraLineItems, extraLineItems, false),
      customLineItems: pickAddedItems(values.customLineItems, customLineItems, true),
    },
  };
};

/**
 * Initial react-hook-form values of the booking form. Adults start at 2 when
 * their minimum is 1; every other guest type starts at its minimum.
 *
 * @param {Array|null} initialDates - Dates from the deep link, if any
 * @param {Array<{name: string, minimum: number}>} guestOptions - Result of `createGuestCount`
 * @returns {{bookingDates: Array, guestCount: Object<string, number>, voucherCode: string, extraLineItems: object, customLineItems: object}}
 */
export const createInitialFormValues = (initialDates, guestOptions) => ({
  bookingDates: initialDates || [null, null],
  guestCount: guestOptions.reduce((acc, guestOption) => ({
    ...acc,
    [guestOption.name]: guestOption.name === 'adult' && guestOption.minimum === 1 ? 2 : guestOption.minimum,
  }), {}),
  voucherCode: '',
  extraLineItems: {},
  customLineItems: {},
});

const hasChanged = (prev, next) => JSON.stringify(prev) !== JSON.stringify(next);

/**
 * Decide how a change of the booking data has to reach the price preview:
 * guest/extra/custom edits can come in rapid bursts and are debounced, a pure
 * date change is sent right away.
 *
 * @param {object} prev - Previous { bookingDates, guestCount, extraLineItems, customLineItems }
 * @param {object} next - Current data of the same shape
 * @returns {'none' | 'immediate' | 'debounced'}
 */
export const detectBookingDataChange = (prev, next) => {
  if (
    hasChanged(prev.guestCount, next.guestCount)
    || hasChanged(prev.extraLineItems, next.extraLineItems)
    || hasChanged(prev.customLineItems, next.customLineItems)
  ) {
    return 'debounced';
  }
  return hasChanged(prev.bookingDates, next.bookingDates) ? 'immediate' : 'none';
};

/**
 * Which dialog a booking deep link opens: the mobile sheet below the desktop
 * layout, the checkout dialog on desktop (only with complete dates).
 *
 * @param {object} params
 * @param {boolean} params.autoOpen - Deep link asked to open the booking flow
 * @param {boolean} params.hasDates - Both dates are known
 * @param {boolean} params.isDesktopLayout
 * @returns {{sheetOpen: boolean, checkoutOpen: boolean}}
 */
export const resolveDeepLinkDialogs = ({ autoOpen, hasDates, isDesktopLayout }) => ({
  sheetOpen: autoOpen && !isDesktopLayout,
  checkoutOpen: autoOpen && hasDates && isDesktopLayout,
});
